import { useState, type ChangeEvent } from 'react';
import { Star } from 'lucide-react';
import { STRINGS } from '@/strings/strings';
import { cn } from '@/lib/utils';
import { ApiError, NetworkError } from '@/lib/api-client';
import { submitRating, type SubmitRatingResponse } from '@/features/story/api';

/**
 * Inline rating widget placed below the asset-buttons on /story/:storyId.
 *
 * Backend wire: POST /api/stories/:storyId/rating with `{ rating, comment? }`.
 * Upsert on the server (re-rating overwrites); comment is optional and
 * capped at 2000 chars by the DTO validator (we enforce the same cap
 * client-side via `maxLength`).
 *
 * State machine:
 *
 *   - initial      — mount state when `initialRating == null`. 5 outline
 *                    stars, no textarea, submit disabled.
 *   - selected     — user has picked a star count. Stars filled up to
 *                    selection, textarea visible, submit enabled.
 *   - submitting   — POST in flight. Button disabled, label "Wird gesendet…".
 *                    Optimistic: the render tree actually shows
 *                    alreadyRated visuals during this window — the user
 *                    sees the completed state before the server responds.
 *                    On error, we fall back to `error`.
 *   - error        — POST failed. Submit button re-enabled with label
 *                    "Nochmal versuchen"; textarea draft preserved so
 *                    the user can retry without retyping. An error
 *                    string is rendered below the widget.
 *   - alreadyRated — terminal state, reached either by remount with
 *                    `initialRating != null` OR by a successful submit
 *                    this session. Stars filled up to the persisted value;
 *                    textarea + submit hidden; "Danke für dein Feedback!"
 *                    subtitle shown; if `ratingText` is present, it is
 *                    rendered below the stars as "Deine Anmerkung: {text}".
 *
 * Star hover affordance (initial / selected / error states only):
 * `hoverStars` (1-5) fills stars 1..N at opacity-60. Clears on mouse-leave.
 * In alreadyRated / submitting state, hover is ignored.
 *
 * Error logging matches the established PWA pattern (Gate-0 F2): catch
 * `ApiError | NetworkError`, `console.error` + surface STRINGS string;
 * no centralized logger.
 */
interface RatingWidgetProps {
  storyId: string;
  initialRating: number | null;
  initialComment: string | null;
  /** Present for consistency with `StoryDetail`; unused in UI today. */
  initialRatedAt: string | null;
  /** Called on successful submit with the server's persisted fields.
   *  StoryRoute uses this to update the `story` prop so subsequent
   *  re-renders see the already-rated state directly. */
  onSubmitted: (result: SubmitRatingResponse) => void;
  /** Optional submit-callback override. Defaults to the audio
   *  `submitRating` (POST /api/stories/:id/rating). The book review
   *  page passes a book-flavoured adapter that posts to
   *  POST /api/book-orders/:id/rating and normalises the response to
   *  the SubmitRatingResponse shape — reusing the widget's state
   *  machine without duplicating the UI. The `storyId` prop in that
   *  case carries the bookOrderId. */
  submit?: (
    entityId: string,
    dto: { rating: number; comment?: string },
  ) => Promise<SubmitRatingResponse>;
  /** Optional headline override. Defaults to the audio-rating headline
   *  ("Wie hat dir die Geschichte gefallen?"). The book review page
   *  passes a book-specific question. The headline also drives the
   *  radiogroup's aria-label. */
  headline?: string;
}

type WidgetState = 'initial' | 'selected' | 'submitting' | 'error' | 'alreadyRated';

const STARS = [1, 2, 3, 4, 5] as const;
const COMMENT_MAX_LEN = 2000;

export function RatingWidget({
  storyId,
  initialRating,
  initialComment,
  initialRatedAt,
  onSubmitted,
  submit,
  headline,
}: RatingWidgetProps) {
  const submitFn = submit ?? submitRating;
  // initialRatedAt is accepted for prop-shape symmetry with StoryDetail.
  // Today's UI doesn't render it; kept here so callers don't need to
  // re-check the shape when a "rated on {date}" line is added later.
  void initialRatedAt;

  const alreadyRatedAtMount = initialRating != null;

  const [widgetState, setWidgetState] = useState<WidgetState>(
    alreadyRatedAtMount ? 'alreadyRated' : 'initial',
  );
  const [selectedStars, setSelectedStars] = useState<number | null>(null);
  const [hoverStars, setHoverStars] = useState<number>(0);
  const [commentDraft, setCommentDraft] = useState<string>('');
  const [persistedStars, setPersistedStars] = useState<number | null>(initialRating);
  const [persistedComment, setPersistedComment] = useState<string | null>(initialComment);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const copy = STRINGS.storyOutput.page.rating;
  const headlineText = headline ?? copy.headline;

  const handleStarClick = (n: number) => {
    if (widgetState === 'alreadyRated' || widgetState === 'submitting') return;
    setSelectedStars(n);
    setWidgetState('selected');
    setErrorMessage(null);
  };

  const handleCommentChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setCommentDraft(e.target.value);
  };

  const handleSubmit = async () => {
    if (selectedStars == null) return;
    const comment = commentDraft.trim() ? commentDraft.trim() : undefined;

    setWidgetState('submitting');
    // Optimistic: seat the persisted values now so the rendered tree
    // transitions to alreadyRated visuals immediately. On error we
    // revert to `error` state and restore the edit affordances.
    setPersistedStars(selectedStars);
    setPersistedComment(comment ?? null);

    try {
      const result = await submitFn(storyId, {
        rating: selectedStars,
        ...(comment ? { comment } : {}),
      });
      setPersistedStars(result.ratingStars);
      setPersistedComment(result.ratingText);
      setWidgetState('alreadyRated');
      setErrorMessage(null);
      onSubmitted(result);
    } catch (err) {
      console.error(err);
      if (err instanceof ApiError || err instanceof NetworkError) {
        setErrorMessage(copy.errors.submitFailed);
      } else {
        setErrorMessage(copy.errors.submitFailed);
      }
      // Revert optimistic commits; keep the draft so the user can retry.
      setPersistedStars(initialRating);
      setPersistedComment(initialComment);
      setWidgetState('error');
    }
  };

  const showAlreadyRated = widgetState === 'alreadyRated' || widgetState === 'submitting';
  const displayStars = showAlreadyRated
    ? (persistedStars ?? 0)
    : hoverStars > 0
      ? hoverStars
      : (selectedStars ?? 0);
  const displayOpacityClass = !showAlreadyRated && hoverStars > 0 ? 'opacity-60' : 'opacity-100';

  return (
    <div className="rounded-xl border border-dream-navy/10 bg-dream-white p-4">
      <p className="text-sm text-dream-navy/70">{headlineText}</p>

      <div
        role="radiogroup"
        aria-label={headlineText}
        className={cn('mt-2 flex items-center gap-1.5', displayOpacityClass)}
        onMouseLeave={() => setHoverStars(0)}
      >
        {STARS.map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={displayStars === n}
            aria-label={copy.ariaLabel.starN.replace('{n}', String(n))}
            onMouseEnter={() => {
              if (!showAlreadyRated) setHoverStars(n);
            }}
            onClick={() => handleStarClick(n)}
            disabled={showAlreadyRated}
            className={cn(
              'flex h-7 w-7 items-center justify-center transition-colors',
              showAlreadyRated && 'cursor-default',
            )}
          >
            <Star
              className="h-6 w-6"
              fill={n <= displayStars ? 'currentColor' : 'none'}
              color={n <= displayStars ? 'var(--color-dream-gold)' : 'currentColor'}
              strokeWidth={1.5}
            />
          </button>
        ))}
      </div>

      {showAlreadyRated ? (
        <>
          {persistedComment && (
            <p className="mt-3 text-sm text-dream-navy/80">
              <span className="font-medium">{copy.alreadyRatedNote}</span> {persistedComment}
            </p>
          )}
          <p className="mt-2 text-xs text-dream-navy/60">
            {widgetState === 'submitting' ? copy.submitting : copy.thanks}
          </p>
        </>
      ) : widgetState === 'selected' || widgetState === 'error' ? (
        <>
          <textarea
            value={commentDraft}
            onChange={handleCommentChange}
            maxLength={COMMENT_MAX_LEN}
            placeholder={copy.placeholder}
            aria-label={copy.ariaLabel.textarea}
            rows={3}
            className="mt-3 w-full resize-none rounded-lg border border-dream-navy/15 bg-dream-background/60 p-3 text-sm text-dream-navy placeholder:text-dream-navy/50 focus:border-dream-navy/40 focus:outline-none"
          />
          <button
            type="button"
            onClick={() => {
              void handleSubmit();
            }}
            disabled={widgetState === 'selected' && selectedStars == null}
            className={cn(
              'mt-3 h-10 w-full rounded-lg bg-dream-navy text-sm font-medium text-dream-white transition hover:bg-dream-navy/90',
              'disabled:cursor-default disabled:opacity-60',
            )}
          >
            {widgetState === 'error' ? copy.retry : copy.submit}
          </button>
          {errorMessage && (
            <p role="alert" className="mt-2 text-xs text-error">
              {errorMessage}
            </p>
          )}
        </>
      ) : null}
    </div>
  );
}
